import { useState, useMemo, useCallback, useEffect } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { ReceiptPoundSterling, CheckCircle2, AlertCircle, ChevronLeft, ChevronRight, Filter, Columns3, PoundSterling } from 'lucide-react';
import { PageWithRightPanel } from '@/components/layout/PageWithRightPanel';
import { RightPanelBase, type RightPanelTab } from '@/components/RightPanel/RightPanelBase';
import { useRightPanel } from '@/contexts/RightPanelContext';
import { TransactionImportFiltersTab } from './transaction-import-filters-tab';
import { TransactionImportColumnsTab } from './transaction-import-columns-tab';
import { TransactionImportValueTab } from './transaction-import-value-tab';
import { StaticMetricCardAlt } from '@/components/StaticMetricCardAlt';
import type { Transaction, TransactionSource } from '@/types/asset-import.types';
import { getCategoryNames } from '@/config/categories';
import { useTransactionCellRenderer } from './hooks/useTransactionCellRenderer';

interface TransactionTableProps {
  readonly transactions: Transaction[];
  readonly selectedTransactionIds: string[];
  readonly onSelectionChange: (ids: string[]) => void;
  readonly onCreateAssets: (useGrossValue: boolean, transactionEdits: Map<string, Partial<Transaction>>) => void;
  readonly onCancel: () => void;
}

export function TransactionTable({ 
  transactions, 
  selectedTransactionIds,
  onSelectionChange,
  onCreateAssets, 
  onCancel 
}: TransactionTableProps) {
  const {
    selectedRowIds,
    setSelectedRowIds,
    visibleColumns: contextVisibleColumns,
    setVisibleColumns,
    columnOrder: contextColumnOrder,
    setColumnOrder,
    tableFilters,
    setTableFilters,
    setActiveTab,
    setIsExpanded,
  } = useRightPanel();

  const [currentPage, setCurrentPage] = useState(1);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSelectionInitialized, setIsSelectionInitialized] = useState(false);
  const [useGrossValue, setUseGrossValue] = useState(false);
  const [transactionEdits, setTransactionEdits] = useState<Map<string, Partial<Transaction>>>(new Map());
  const [splitTransactions, setSplitTransactions] = useState<Set<string>>(new Set());
  const pageSize = 25;

  // Get available categories
  const availableCategories = useMemo(() => getCategoryNames(), []);

  // Default column configuration
  const DEFAULT_COLUMNS = useMemo(() => [
    { id: 'transactionReference', label: 'Reference', visible: true, order: 0 },
    { id: 'transactionDate', label: 'Date', visible: true, order: 1 },
    { id: 'transactionType', label: 'Type', visible: true, order: 2 },
    { id: 'transactionSource', label: 'Source', visible: false, order: 3 },
    { id: 'assetCode', label: 'Asset Code', visible: true, order: 4 },
    { id: 'description', label: 'Description', visible: true, order: 5 },
    { id: 'category', label: 'Category', visible: true, order: 6 },
    { id: 'netValue', label: 'Net Value', visible: true, order: 7 },
    { id: 'vatAmount', label: 'VAT', visible: false, order: 8 },
    { id: 'grossValue', label: 'Gross Value', visible: true, order: 9 },
    { id: 'supplier', label: 'Supplier', visible: false, order: 10 },
    { id: 'analysisCode', label: 'Analysis Code', visible: false, order: 11 },
    { id: 'nominalAccount', label: 'Nominal Account', visible: false, order: 12 },
    { id: 'costCentre', label: 'Cost Centre', visible: false, order: 13 },
    { id: 'project', label: 'Project', visible: false, order: 14 },
    { id: 'quantity', label: 'Quantity', visible: true, order: 15 },
    { id: 'split', label: 'Split', visible: true, order: 16 },
    { id: 'serialNumber', label: 'Serial Number', visible: false, order: 17 },
    { id: 'status', label: 'Status', visible: true, order: 18 },
  ], []);

  // Initialize columns on mount
  useEffect(() => {
    if (!isInitialized) {
      // Always set default visible columns for transaction table
      const defaultVisible = DEFAULT_COLUMNS.filter(col => col.visible).map(col => col.id);
      setVisibleColumns(defaultVisible);
      
      if (contextColumnOrder.length === 0) {
        setColumnOrder(DEFAULT_COLUMNS.map(col => col.id));
      }
      setIsInitialized(true);
    }
  }, [isInitialized, contextColumnOrder.length, DEFAULT_COLUMNS, setVisibleColumns, setColumnOrder]);

  // Sync selectedTransactionIds with selectedRowIds
  useEffect(() => {
    if (!isSelectionInitialized && transactions.length > 0) {
      const eligibleIds = transactions
        .filter(tx => tx.status === 'eligible' && tx.canCreateAsset)
        .map(tx => tx.id);
      setSelectedRowIds(new Set(eligibleIds));
      setIsSelectionInitialized(true);
    }
  }, [isSelectionInitialized, transactions, setSelectedRowIds]);

  // Update parent when selection changes
  useEffect(() => {
    onSelectionChange(Array.from(selectedRowIds));
  }, [selectedRowIds, onSelectionChange]);

  // Get ordered visible columns
  const orderedVisibleColumns = useMemo(() => {
    const visibleCols = contextVisibleColumns.length > 0 ? contextVisibleColumns : DEFAULT_COLUMNS.filter(c => c.visible).map(c => c.id);
    const order = contextColumnOrder.length > 0 ? contextColumnOrder : DEFAULT_COLUMNS.map(c => c.id);
    
    return order
      .filter(id => visibleCols.includes(id))
      .map(id => DEFAULT_COLUMNS.find(col => col.id === id))
      .filter((col): col is typeof DEFAULT_COLUMNS[0] => col !== undefined);
  }, [contextVisibleColumns, contextColumnOrder, DEFAULT_COLUMNS]);

  // Apply filters
  const filteredTransactions = useMemo(() => {
    let filtered = [...transactions];

    // Filter by "show excluded" (already used)
    if (!tableFilters.showExcluded) {
      filtered = filtered.filter(tx => tx.status !== 'already-used');
    }

    // Filter by categories
    if (tableFilters.selectedCategories && tableFilters.selectedCategories.length > 0) {
      filtered = filtered.filter(tx => 
        tx.category && tableFilters.selectedCategories.includes(tx.category)
      );
    }

    // Filter by transaction source
    if (tableFilters.transactionSource) {
      filtered = filtered.filter(tx => tx.transactionSource === tableFilters.transactionSource);
    }

    // Filter by supplier
    if (tableFilters.supplier) {
      filtered = filtered.filter(tx => 
        tx.supplier && tx.supplier.toLowerCase().includes(tableFilters.supplier.toLowerCase())
      );
    }

    return filtered;
  }, [transactions, tableFilters]);

  // Calculate summary statistics
  const summary = useMemo(() => {
    const eligible = transactions.filter(tx => tx.status === 'eligible' && tx.canCreateAsset);
    const selected = Array.from(selectedRowIds).map(id => transactions.find(tx => tx.id === id)).filter(Boolean) as Transaction[];
    const alreadyUsed = transactions.filter(tx => tx.status === 'already-used');
    const excluded = transactions.length - eligible.length;

    return {
      eligible: eligible.length,
      selected: selected.length,
      alreadyUsed: alreadyUsed.length,
      excluded,
    };
  }, [transactions, selectedRowIds]);

  // Pagination
  const totalPages = Math.ceil(filteredTransactions.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedTransactions = filteredTransactions.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [tableFilters]);

  // Selection handlers
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      const selectableIds = paginatedTransactions
        .filter(tx => tx.canCreateAsset && tx.status === 'eligible')
        .map(tx => tx.id);
      setSelectedRowIds(new Set(selectableIds));
    } else {
      setSelectedRowIds(new Set());
    }
  }, [paginatedTransactions, setSelectedRowIds]);

  const handleSelectionChange = useCallback((txId: string, checked: boolean) => {
    setSelectedRowIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(txId);
      } else {
        newSet.delete(txId);
      }
      return newSet;
    });
  }, [setSelectedRowIds]);

  const allSelected = paginatedTransactions
    .filter(tx => tx.canCreateAsset && tx.status === 'eligible')
    .every(tx => selectedRowIds.has(tx.id)) && paginatedTransactions.some(tx => tx.canCreateAsset && tx.status === 'eligible');

  // Edit handlers
  const handleEditField = useCallback((txId: string, field: keyof Transaction, value: string) => {
    setTransactionEdits(prev => {
      const newEdits = new Map(prev);
      const currentEdit = newEdits.get(txId) || {};
      newEdits.set(txId, { ...currentEdit, [field]: value });
      return newEdits;
    });
  }, []);

  const getDisplayValue = useCallback((tx: Transaction, field: keyof Transaction) => {
    const edits = transactionEdits.get(tx.id);
    if (edits && field in edits) {
      return edits[field] as string;
    }
    return tx[field] as string;
  }, [transactionEdits]);

  // Split toggle handler
  const handleSplitToggle = useCallback((txId: string, enabled: boolean) => {
    setSplitTransactions(prev => {
      const newSet = new Set(prev);
      if (enabled) {
        newSet.add(txId);
      } else {
        newSet.delete(txId);
      }
      return newSet;
    });
  }, []);

  // Check if a transaction can be split (quantity > 1 and eligible)
  const canSplitTransaction = useCallback((tx: Transaction): boolean => {
    return tx.canCreateAsset && 
           tx.status === 'eligible' && 
           tx.quantity !== undefined && 
           tx.quantity > 1;
  }, []);

  // Create assets handler - pass edits along with transactions including split info
  const handleCreateAssets = (): void => {
    // Merge split information into transaction edits
    const editsWithSplit = new Map(transactionEdits);
    splitTransactions.forEach(txId => {
      const existingEdit = editsWithSplit.get(txId) ?? {};
      editsWithSplit.set(txId, { ...existingEdit, splitIntoIndividual: true } as Partial<Transaction>);
    });
    onCreateAssets(useGrossValue, editsWithSplit);
  };

  // Format currency
  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '-';
    }
    return value.toLocaleString('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Get unique categories and suppliers for filters
  const categories = useMemo(() => {
    return Array.from(new Set(transactions.map(tx => tx.category).filter(Boolean) as string[])).sort();
  }, [transactions]);

  const suppliers = useMemo(() => {
    return Array.from(new Set(transactions.map(tx => tx.supplier).filter(Boolean) as string[])).sort();
  }, [transactions]);

  // Get transaction type label
  const getTransactionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'INV': 'Invoice',
      'CRN': 'Credit Note',
      'DR': 'Debit',
      'CR': 'Credit',
      'CSTTIM': 'Timesheet',
      'CSTMSC': 'Miscellaneous'
    };
    return labels[type] || type;
  };

  // Get transaction source label
  const getTransactionSourceLabel = (source: TransactionSource) => {
    const labels: Record<TransactionSource, string> = {
      'purchase-ledger': 'Purchase Ledger',
      'nominal-ledger': 'Nominal Ledger',
      'costing': 'Costing'
    };
    return labels[source] || source;
  };

  // Use the cell renderer hook to reduce cognitive complexity
  const { renderCell } = useTransactionCellRenderer({
    getDisplayValue,
    handleEditField,
    availableCategories,
    formatCurrency,
    canSplitTransaction,
    splitTransactions,
    handleSplitToggle,
    getTransactionTypeLabel,
    getTransactionSourceLabel,
  });

  // Right panel tabs
  const rightPanelTabs: RightPanelTab[] = [
    {
      id: 'filters',
      label: 'Filters',
      icon: Filter,
      content: (
        <TransactionImportFiltersTab
          categories={categories}
          suppliers={suppliers}
          showExcluded={tableFilters.showExcluded ?? false}
          selectedCategories={tableFilters.selectedCategories ?? []}
          transactionSource={tableFilters.transactionSource}
          supplier={tableFilters.supplier}
          onShowExcludedChange={(show) => setTableFilters({ ...tableFilters, showExcluded: show })}
          onCategoriesChange={(cats) => setTableFilters({ ...tableFilters, selectedCategories: cats })}
          onTransactionSourceChange={(source) => setTableFilters({ ...tableFilters, transactionSource: source })}
          onSupplierChange={(sup) => setTableFilters({ ...tableFilters, supplier: sup })}
        />
      ),
    },
    {
      id: 'columns',
      label: 'Columns',
      icon: Columns3,
      content: (
        <TransactionImportColumnsTab
          columns={DEFAULT_COLUMNS}
          visibleColumns={contextVisibleColumns.length > 0 ? contextVisibleColumns : DEFAULT_COLUMNS.filter(c => c.visible).map(c => c.id)}
          columnOrder={contextColumnOrder.length > 0 ? contextColumnOrder : DEFAULT_COLUMNS.map(c => c.id)}
          onVisibleColumnsChange={setVisibleColumns}
          onColumnOrderChange={setColumnOrder}
        />
      ),
      badgeCount: orderedVisibleColumns.length,
    },
    {
      id: 'value',
      label: 'Value',
      icon: PoundSterling,
      content: (
        <TransactionImportValueTab
          useGrossValue={useGrossValue}
          onUseGrossValueChange={setUseGrossValue}
        />
      ),
    },
  ];

  return (
    <PageWithRightPanel>
      <div className="flex h-full w-full flex-col overflow-hidden">
        {/* KPI Cards Section - Separate from table */}
        <div className="flex-shrink-0 p-6 bg-background">
          <div className="grid grid-cols-4 gap-4">
            {/* Eligible Transactions */}
            <StaticMetricCardAlt
              title="Eligible Transactions"
              value={summary.eligible.toString()}
              change="Available to create assets"
              changeLabel=""
              icon={ReceiptPoundSterling}
              variant="success"
            />

            {/* Selected */}
            <StaticMetricCardAlt
              title="Selected"
              value={summary.selected.toString()}
              change="Selected for asset creation"
              changeLabel=""
              icon={CheckCircle2}
              variant="info"
            />

            {/* Already Used */}
            <StaticMetricCardAlt
              title="Already Used"
              value={summary.alreadyUsed.toString()}
              change="Previously used"
              changeLabel=""
              icon={CheckCircle2}
              variant="info"
            />

            {/* Excluded */}
            <StaticMetricCardAlt
              title="Excluded"
              value={summary.excluded.toString()}
              change="Cannot create assets"
              changeLabel=""
              icon={AlertCircle}
              variant="warning"
            />
          </div>
        </div>

        {/* Table Section Header */}
        <div className="px-6 py-4 bg-white dark:bg-black border-t border-b border-slate-200 dark:border-slate-800">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Review Transactions
          </h3>
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
            Select the transactions you want to use to create assets. Transactions already used are disabled.
          </p>
        </div>

        {/* Table Container - Full width, no padding */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0 bg-white dark:bg-black">
          <div className="flex-1 overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
                <TableRow className="border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                  {/* Checkbox column */}
                  <TableHead className="h-12 font-semibold relative w-20">
                    <div className="flex items-center justify-center px-3">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={handleSelectAll}
                      />
                    </div>
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 h-6">
                      <div className="w-px h-full bg-slate-300 dark:bg-slate-600" />
                    </div>
                  </TableHead>

                  {/* Dynamic columns */}
                  {orderedVisibleColumns.map((column) => (
                    <TableHead
                      key={column.id}
                      className={`h-12 font-semibold relative ${
                        ['netValue', 'vatAmount', 'grossValue', 'quantity'].includes(column.id) ? 'text-right' : ''
                      }`}
                    >
                      {column.label}
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 h-6">
                        <div className="w-px h-full bg-slate-300 dark:bg-slate-600" />
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>

              <TableBody>
                {paginatedTransactions.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={orderedVisibleColumns.length + 1}
                      className="h-96"
                    >
                      <div className="flex flex-col items-center justify-center space-y-4 py-12">
                        <div className="rounded-full bg-slate-100 dark:bg-slate-800 p-6">
                          <ReceiptPoundSterling className="h-12 w-12 text-slate-400 dark:text-slate-500" />
                        </div>
                        <div className="space-y-2 text-center">
                          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                            No transactions found
                          </h3>
                          <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                            {filteredTransactions.length !== transactions.length
                              ? "Try adjusting your filters to find what you're looking for"
                              : "No eligible transactions available"}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedTransactions.map((tx, index) => (
                    <TableRow
                      key={tx.id}
                      className={`
                        ${index % 2 === 0
                          ? 'bg-white dark:bg-slate-950'
                          : 'bg-slate-50/50 dark:bg-slate-900/50'
                        }
                        hover:bg-slate-100 dark:hover:bg-slate-800
                        transition-colors
                        border-b border-slate-100 dark:border-slate-800
                        ${tx.status === 'already-used' ? 'opacity-50' : ''}
                      `}
                    >
                      {/* Checkbox cell */}
                      <TableCell className="w-20">
                        <div className="flex items-center justify-center">
                          <Checkbox
                            checked={selectedRowIds.has(tx.id)}
                            onCheckedChange={(checked) =>
                              handleSelectionChange(tx.id, checked as boolean)
                            }
                            disabled={!tx.canCreateAsset || tx.status === 'already-used'}
                          />
                        </div>
                      </TableCell>

                      {/* Dynamic cells - rendered via hook to reduce cognitive complexity */}
                      {orderedVisibleColumns.map((column) => renderCell(column.id, tx))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Footer - Fixed */}
        <div className="border-t bg-card px-6 py-4 flex-shrink-0">
          {/* Row 1: Entry count - right aligned */}
          <div className="text-sm text-muted-foreground text-right mb-3">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredTransactions.length)} of {filteredTransactions.length} transactions
            {filteredTransactions.length !== transactions.length && ` (filtered from ${transactions.length} total)`}
          </div>

          {/* Row 2: Action buttons on left, Pagination on right */}
          <div className="flex items-center justify-between">
            {/* Left: Action buttons */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateAssets}
                disabled={selectedRowIds.size === 0}
              >
                Create Assets {selectedRowIds.size > 0 ? `(${selectedRowIds.size})` : ''}
              </Button>
            </div>

            {/* Right: Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>

                <div className="flex items-center gap-1">
                  <span className="text-sm text-muted-foreground px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <RightPanelBase tabs={rightPanelTabs} defaultTab="filters" />
    </PageWithRightPanel>
  );
}

export default TransactionTable;
